/**
 * The invoicing-profile fields, shared by every door that sells a deployment: the
 * signup wizard's billing step and the "Aggiungi workspace" purchase in the Tray
 * Account. One fieldset because it is ONE fiscal rule (`lib/billingProfile`) — a second
 * copy is how the two doors would start asking for different things.
 *
 * Required fields carry a `*` and, once touched, a named error; the caller gates its own
 * submit on `billingComplete(...)`. The backend refuses an incomplete profile anyway
 * (`billing:incomplete:<fields>`) — this only means the buyer learns it before paying.
 */
import type { TFunction } from "i18next";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/cn";
import {
  type BillingForm,
  isBusiness as isBusinessOf,
  isItaly as isItalyOf,
  missingBillingFields,
  showEinvoicing as showEinvoicingOf,
} from "@/lib/billingProfile";

import { CountryCombobox } from "@/pages/onboarding/CountryCombobox";

function Required() {
  return <span className="text-danger"> *</span>;
}

export function BillingFieldset({
  t,
  billing,
  setB,
  showErrors = false,
}: {
  t: TFunction;
  billing: BillingForm;
  setB: <K extends keyof BillingForm>(key: K, value: BillingForm[K]) => void;
  /** Turn the missing-field hints on — the caller flips it once the buyer has tried
   *  to move on, so a pristine form is not a wall of red. */
  showErrors?: boolean;
}) {
  const business = isBusinessOf(billing);
  const italy = isItalyOf(billing);
  const einvoice = showEinvoicingOf(billing);
  const missing = new Set(showErrors ? missingBillingFields(billing) : []);
  const bad = (key: string) => (missing.has(key) ? "border-danger" : undefined);

  return (
    <>
      {/* Privato vs business — the flag that gates VAT, the name shape and e-invoicing. */}
      <div className="space-y-1.5">
        <Label>{t("onboarding.billing.entityType", "Tipo di cliente")}</Label>
        <div className="grid grid-cols-2 gap-2">
          {(["business", "private"] as const).map((kind) => (
            <button
              key={kind}
              type="button"
              onClick={() => setB("entity_type", kind)}
              className={cn(
                "rounded-md border px-3 py-2 text-sm font-medium transition-colors",
                billing.entity_type === kind
                  ? "border-primary bg-primary/[0.06] text-foreground"
                  : "border-border/70 text-muted-foreground hover:border-border",
              )}
            >
              {kind === "business"
                ? t("onboarding.billing.business", "Azienda")
                : t("onboarding.billing.private", "Privato")}
            </button>
          ))}
        </div>
      </div>

      {business ? (
        <div className="space-y-1.5">
          <Label htmlFor="legal_name">
            {t("onboarding.billing.legalName", "Ragione sociale")}
            <Required />
          </Label>
          <Input
            id="legal_name"
            value={billing.legal_name}
            className={bad("legal_name")}
            onChange={(e) => setB("legal_name", e.target.value)}
          />
        </div>
      ) : (
        /* Split apart on purpose: the invoice header is "Nome Cognome", and a single
           free-text field cannot be split back reliably. */
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="first_name">
              {t("onboarding.billing.firstName", "Nome")}
              <Required />
            </Label>
            <Input
              id="first_name"
              value={billing.first_name}
              className={bad("first_name")}
              onChange={(e) => setB("first_name", e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="last_name">
              {t("onboarding.billing.lastName", "Cognome")}
              <Required />
            </Label>
            <Input
              id="last_name"
              value={billing.last_name}
              className={bad("last_name")}
              onChange={(e) => setB("last_name", e.target.value)}
            />
          </div>
        </div>
      )}

      <div className="space-y-1.5">
        <Label htmlFor="country">
          {t("onboarding.billing.country", "Paese")}
          <Required />
        </Label>
        <CountryCombobox
          id="country"
          value={billing.country}
          onChange={(code) => setB("country", code)}
          placeholder={t("onboarding.billing.countrySearch", "Cerca un paese…")}
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2 space-y-1.5">
          <Label htmlFor="street">
            {t("onboarding.billing.street", "Indirizzo")}
            <Required />
          </Label>
          <Input
            id="street"
            value={billing.street}
            className={bad("street")}
            onChange={(e) => setB("street", e.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="city">
            {t("onboarding.billing.city", "Città")}
            <Required />
          </Label>
          <Input
            id="city"
            value={billing.city}
            className={bad("city")}
            onChange={(e) => setB("city", e.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="zip">
            {t("onboarding.billing.zip", "CAP")}
            <Required />
          </Label>
          <Input
            id="zip"
            value={billing.zip}
            className={bad("zip")}
            onChange={(e) => setB("zip", e.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="province">
            {t("onboarding.billing.province", "Provincia")}
            {italy && <Required />}
          </Label>
          <Input
            id="province"
            value={billing.province}
            className={bad("province")}
            onChange={(e) => setB("province", e.target.value)}
          />
        </div>
      </div>

      {business && (
        <div className="space-y-1.5">
          <Label htmlFor="vat_number">
            {t("onboarding.billing.vat", "Partita IVA")}
            <Required />
          </Label>
          <Input
            id="vat_number"
            value={billing.vat_number}
            className={bad("vat_number")}
            onChange={(e) => setB("vat_number", e.target.value)}
          />
        </div>
      )}

      {/* The codice fiscale is always offered — it is mandatory only for an Italian
          private (a foreign one simply has none to give). */}
      <div className="space-y-1.5">
        <Label htmlFor="tax_code">
          {t("onboarding.billing.taxCode", "Codice fiscale")}
          {italy && !business && <Required />}
        </Label>
        <Input
          id="tax_code"
          value={billing.tax_code}
          className={bad("tax_code")}
          onChange={(e) => setB("tax_code", e.target.value)}
        />
      </div>

      {einvoice && (
        <div
          className={cn(
            "grid grid-cols-2 gap-3 rounded-lg border border-border/60 bg-surface/40 p-3",
            missing.has("sdi_or_pec") && "border-danger",
          )}
        >
          <p className="col-span-2 text-xs font-medium text-muted-foreground">
            {t("onboarding.billing.einvoiceTitle", "Fatturazione elettronica")}
            <Required />
          </p>
          <div className="space-y-1.5">
            <Label htmlFor="sdi_code">{t("onboarding.billing.sdi", "Codice destinatario")}</Label>
            <Input
              id="sdi_code"
              value={billing.sdi_code}
              onChange={(e) => setB("sdi_code", e.target.value.toUpperCase().slice(0, 7))}
              placeholder="0000000"
              maxLength={7}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="pec">{t("onboarding.billing.pec", "PEC")}</Label>
            <Input id="pec" value={billing.pec} onChange={(e) => setB("pec", e.target.value)} />
          </div>
          <p className="col-span-2 text-[11px] text-muted-foreground">
            {t(
              "onboarding.billing.sdiHelp",
              "Inserisci il codice SDI a 7 caratteri oppure la PEC: ne basta uno.",
            )}
          </p>
        </div>
      )}

      {showErrors && missing.size > 0 && (
        <p className="text-xs text-danger">
          {t("onboarding.billing.incomplete", "Compila i campi obbligatori per la fattura.")}
        </p>
      )}
    </>
  );
}
